import React from "react";
import { type Task, taskPriorities, taskCategories } from "@/components/Calendar/scheduleData";
import { Clock, CheckCircle2, AlertCircle, Circle } from "lucide-react";

interface TaskCardProps {
  task: Task;
  size?: "micro" | "small" | "medium" | "large";
  isDarkMode?: boolean;
  onEdit?: (taskId: string, task: Task) => void;
  showNotification?: boolean;
}

export function TaskCard({
  task,
  size = "medium",
  isDarkMode = false,
  onEdit,
  showNotification = false,
}: TaskCardProps) {
  const handleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onEdit) {
      onEdit(task.id, task);
    }
  };

  const getPriorityConfig = () => {
    return (
      taskPriorities.find((p) => p.value === task.priority) || taskPriorities[1]
    );
  };

  const getCategoryConfig = () => {
    return (
      taskCategories.find((c) => c.value === task.category) || taskCategories[0]
    );
  };

  const getStatusIcon = () => {
    switch (task.status) {
      case "completed":
        return <CheckCircle2 className="w-3 h-3 text-green-500" />;
      case "in_progress":
        return <Circle className="w-3 h-3 text-blue-500 fill-blue-500" />;
      case "cancelled":
        return <AlertCircle className="w-3 h-3 text-red-500" />;
      default:
        return <Circle className="w-3 h-3 text-gray-400" />;
    }
  };

  const getStatusLabel = () => {
    switch (task.status) {
      case "completed":
        return "Completed";
      case "in_progress":
        return "In Progress";
      case "cancelled":
        return "Cancelled";
      default:
        return "To Do";
    }
  };

  const priorityConfig = getPriorityConfig();
  const categoryConfig = getCategoryConfig();

  // Micro variant - very compact for week view
  if (size === "micro") {
    return (
      <div
        className={`group relative pointer-events-auto cursor-pointer`}
        onClick={handleClick}
      >
        <div
          className={`relative flex flex-col gap-0.5 pl-2 pr-2 py-1 rounded text-xs border-2 border-dashed ${
            isDarkMode
              ? "bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700 hover:border-gray-500"
              : "bg-white border-gray-300 text-gray-900 hover:bg-gray-50 hover:border-gray-400"
          } hover:shadow-lg hover:scale-105 transition-all duration-200 w-full overflow-hidden ${
            showNotification ? "animate-[pulse_0.5s_ease-in-out_2]" : ""
          }`}
          style={{
            borderLeftWidth: "3px",
            borderLeftStyle: "solid",
            borderLeftColor: priorityConfig.color
              .replace("bg-", "")
              .includes("gray")
              ? "#6B7280"
              : priorityConfig.color.replace("bg-", "").includes("blue")
                ? "#3B82F6"
                : priorityConfig.color.replace("bg-", "").includes("orange")
                  ? "#F97316"
                  : "#EF4444",
          }}
          title=""
        >
          {/* Task checkbox icon - left border indicator */}
          <div
            className={`absolute left-0 top-0 bottom-0 w-1 ${priorityConfig.color} rounded-l`}
          />

          {/* Priority indicator dot */}
          <div
            className={`absolute top-1 right-1 w-2 h-2 rounded-full ${priorityConfig.color} transition-transform duration-200 group-hover:scale-150`}
          />

          {/* Task content */}
          <div className="flex flex-col gap-0.5 min-h-0 flex-1">
            <div className="flex items-center gap-1">
              {getStatusIcon()}
              <div className="font-medium text-[11px] leading-tight truncate flex-1">
                {categoryConfig.icon} {task.title}
              </div>
            </div>

            {task.dueTime && (
              <div className="opacity-75 text-[10px] leading-tight flex items-center gap-1">
                <Clock className="w-2.5 h-2.5" />
                {task.dueTime}
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  // Large variant - for day view
  if (size === "large") {
    return (
      <div
        className={`group relative pointer-events-auto cursor-pointer mb-2`}
        onClick={handleClick}
      >
        <div
          className={`relative pl-3 pr-3 py-3 rounded-lg border-2 border-dashed ${
            isDarkMode
              ? "bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700 hover:border-gray-500"
              : "bg-white border-gray-300 text-gray-900 hover:bg-gray-50 hover:border-gray-400"
          } hover:shadow-xl hover:scale-[1.02] transition-all duration-200 overflow-hidden ${
            showNotification ? "animate-[pulse_0.5s_ease-in-out_2]" : ""
          }`}
          style={{
            borderLeftWidth: "4px",
            borderLeftStyle: "solid",
            borderLeftColor: priorityConfig.color
              .replace("bg-", "")
              .includes("gray")
              ? "#6B7280"
              : priorityConfig.color.replace("bg-", "").includes("blue")
                ? "#3B82F6"
                : priorityConfig.color.replace("bg-", "").includes("orange")
                  ? "#F97316"
                  : "#EF4444",
          }}
          title=""
        >
          {/* Priority badge */}
          <div
            className={`absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-medium ${priorityConfig.bgLight} ${priorityConfig.textColor} transition-transform duration-200 group-hover:scale-110`}
          >
            {priorityConfig.label}
          </div>

          <div className="flex items-start gap-3">
            <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-500 text-white text-lg flex-shrink-0 transition-transform duration-200 group-hover:scale-110 group-hover:rotate-6">
              {categoryConfig.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                {getStatusIcon()}
                <span className="font-medium text-sm opacity-75">
                  {getStatusLabel()}
                </span>
              </div>
              <div className="font-semibold text-base mb-1 truncate">
                {task.title}
              </div>
              {task.description && (
                <div className="text-sm opacity-75 mb-2 line-clamp-2">
                  {task.description}
                </div>
              )}
              <div className="flex items-center gap-3 text-sm opacity-75">
                <div className="flex items-center gap-1">
                  <Clock className="w-4 h-4" />
                  {task.dueTime || "All day"}
                </div>
                <div>
                  →{" "}
                  {task.assignedTo || (
                    <span className="italic text-orange-500">Unassigned</span>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Medium/Small variant - default
  return (
    <div
      className={`group relative pointer-events-auto cursor-pointer`}
      onClick={handleClick}
    >
      <div
        className={`relative text-sm pl-2 pr-2 py-1.5 rounded border-2 border-dashed ${
          isDarkMode
            ? "bg-gray-800 border-gray-600 text-gray-200 hover:bg-gray-700 hover:border-gray-500"
            : "bg-white border-gray-300 text-gray-900 hover:bg-gray-50 hover:border-gray-400"
        } hover:shadow-lg hover:scale-[1.03] transition-all duration-200 overflow-hidden flex flex-col gap-1 ${
          showNotification ? "animate-[pulse_0.5s_ease-in-out_2]" : ""
        }`}
        style={{
          borderLeftWidth: "3px",
          borderLeftStyle: "solid",
          borderLeftColor: priorityConfig.color
            .replace("bg-", "")
            .includes("gray")
            ? "#6B7280"
            : priorityConfig.color.replace("bg-", "").includes("blue")
              ? "#3B82F6"
              : priorityConfig.color.replace("bg-", "").includes("orange")
                ? "#F97316"
                : "#EF4444",
        }}
        title=""
      >
        {/* Priority indicator */}
        <div
          className={`absolute top-1 right-1 w-2 h-2 rounded-full ${priorityConfig.color} transition-transform duration-200 group-hover:scale-150`}
        />

        <div className="flex items-start gap-1.5">
          <div className="flex-shrink-0 mt-0.5">{getStatusIcon()}</div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-1 mb-0.5">
              <span className="text-sm">{categoryConfig.icon}</span>
              <div className="font-medium text-xs truncate leading-tight flex-1">
                {task.title}
              </div>
            </div>
            {task.dueTime && (
              <div className="opacity-75 text-[11px] leading-tight flex items-center gap-1">
                <Clock className="w-3 h-3" />
                {task.dueTime}
              </div>
            )}
            <div className="opacity-75 text-[11px] leading-tight truncate mt-0.5">
              →{" "}
              {task.assignedTo || (
                <span className="italic text-orange-500">Unassigned</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
